// Today.jsx — the Pronto morning home (redesign of Daily).
//
// Same data as the legacy Daily screen (weather, active-bag brew preview, most-
// urgent expiring item, tonight suggestion, shop count) — reshaped into the
// Glass system and the four View modes (docs/pronto-redesign.md §6):
//   adaptive (default) — AI layout + a "showing your morning view" banner
//   ai                 — one composed "Pronto suggests" plan + Ask bar
//   hybrid             — an "Oggi:" strip + a 2×2 metric grid
//   traditional        — a 2×2 metric grid + a passive Ask row
// Only presentation changes; all logic is reused from lib/* and computeMorningBrew.
import { useEffect, useState, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import {
  GlassCard, SolidCard, PrimaryButton, SecondaryButton, Eyebrow, LargeTitle,
} from "../components/pronto";
import { getWeather, timeOfDay, brewSuggestionFromWeather } from "../lib/weather";
import { useSoonItems } from "../lib/pantry";
import { findActiveBags, forecastDepletion } from "../lib/brew-helpers";
import { computeMorningBrew } from "../components/MorningBrew";
import { greeting as i18nGreeting, normalizeLocale, normalizeViewMode, IT } from "../lib/i18n";
import { loadUsage, rankMetrics, PHASE_LABEL } from "../lib/usage";

export function Today({ store, onNavigate }) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => { try { const w = await getWeather(); if (!cancelled) setWeather(w); } catch {} };
    load();
    const id = setInterval(load, 20 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const locale = normalizeLocale(store.preferences?.language);
  const viewMode = normalizeViewMode(store.preferences?.view_mode);

  // Adaptive signal: current phase + this user's recent usage (for card ranking).
  const phase = timeOfDay();
  const [usage, setUsage] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadUsage(store.userId).then(e => { if (!cancelled) setUsage(e); });
    return () => { cancelled = true; };
  }, [store.userId]);

  // --- Data (mirrors Daily) ---------------------------------------------------
  const hour = new Date().getHours();
  const greeting = i18nGreeting(hour, locale);
  const dayName = new Date().toLocaleDateString(locale === "it" ? "it-IT" : "en-US", { weekday: "long" });

  const brewSugg = useMemo(() => brewSuggestionFromWeather(weather), [weather]);
  const expiring = useMemo(() => useSoonItems(store.inventory, store.skus).slice(0, 3), [store.inventory, store.skus]);
  const mostUrgent = expiring[0];

  const activeBag = findActiveBags(store.beans)[0];
  const forecast = activeBag ? forecastDepletion(activeBag, store.brewLogs) : null;
  const previewMode = brewSugg?.suggested_mode === "cold" ? "cold" : brewSugg?.suggested_mode === "iced" ? "iced" : "hot";
  const previewBrew = useMemo(
    () => (activeBag ? computeMorningBrew({
      bean: activeBag,
      cups: store.preferences?.default_cups || 8,
      mode: previewMode,
      tasteProfile: store.tasteProfile,
      defaultProfileId: store.preferences?.default_brew_profile,
    }) : null),
    [activeBag, previewMode, store.tasteProfile, store.preferences?.default_brew_profile, store.preferences?.default_cups]
  );

  const shoppingPending = (store.shoppingList || []).filter(i => !i.done).length;

  const tonightSugg = useMemo(() => {
    const recs = store.recipes || [];
    if (recs.length === 0) return null;
    const expiringNames = new Set(expiring.map(e => String(e.sku?.canonical_name || "").toLowerCase()).filter(Boolean));
    return recs.map(r => {
      const ings = r.ingredients || [];
      const matches = ings.filter(i => [...expiringNames].some(en => String(i.name || "").toLowerCase().includes(en))).length;
      return { recipe: r, matches };
    }).sort((a, b) => b.matches - a.matches || (b.recipe.times_made || 0) - (a.recipe.times_made || 0))[0]?.recipe || null;
  }, [store.recipes, expiring]);

  const urgentName = mostUrgent?.sku?.canonical_name;
  const brewLine = previewBrew ? `${previewBrew.dose_g}g · Ode ${previewBrew.grind.setting} · ${previewBrew.temp_f}°F` : null;

  // Metrics reused by hybrid/traditional grids.
  const metricList = [
    {
      key: "coffee", label: IT.spesa ? "Caffè" : "Coffee",
      value: activeBag ? activeBag.name : "No bag open",
      detail: activeBag ? (brewLine || (forecast ? `~${forecast.brews_remaining} brews left` : "Tap to dial in")) : "Add a bag to start",
      onClick: () => onNavigate("coffee"),
    },
    {
      key: "use", label: "Use",
      value: urgentName || "All fresh",
      detail: mostUrgent
        ? (mostUrgent.days_until_expiry < 0 ? "expired" : mostUrgent.days_until_expiry === 0 ? "today" : mostUrgent.days_until_expiry === 1 ? "tomorrow" : `${mostUrgent.days_until_expiry}d left`)
        : "Nothing expiring soon",
      warn: !!mostUrgent, onClick: () => onNavigate("pantry"),
    },
    {
      key: "tonight", label: "Tonight",
      value: tonightSugg ? tonightSugg.title : "Open",
      detail: tonightSugg ? (tonightSugg.total_min ? `${tonightSugg.total_min} min` : "ready") : "Pick a mood",
      onClick: () => onNavigate("tonight"),
    },
    {
      key: "spesa", label: "Spesa",
      value: shoppingPending > 0 ? `${shoppingPending} item${shoppingPending !== 1 ? "s" : ""}` : "Empty",
      detail: shoppingPending > 0 ? "to buy" : "list clear",
      onClick: () => onNavigate("shopping"),
    },
  ];

  // Adaptive: order the cards by time-of-day, refined by personal usage.
  const metrics = rankMetrics(usage, phase, hour)
    .map(k => metricList.find(m => m.key === k))
    .filter(Boolean);

  // Composed natural-language plan (deterministic template, not AI).
  const suggestion = useMemo(() => {
    const parts = [];
    if (activeBag) parts.push(previewBrew ? `Brew your **${activeBag.name}** (${previewBrew.dose_g}g, Ode ${previewBrew.grind.setting})` : `Brew your **${activeBag.name}**`);
    if (tonightSugg) parts.push(`${parts.length ? "then " : "Make "}**${tonightSugg.title}** tonight`);
    let sentence = parts.join(", ");
    if (tonightSugg && tonightSugg.matches > 0 && urgentName) sentence += ` — it uses the **${urgentName}** that's turning`;
    if (!sentence) sentence = "Add a bag and a few recipes and Pronto will plan your morning.";
    else sentence += ".";
    const shop = shoppingPending > 0 ? `You'll need **${shoppingPending} item${shoppingPending !== 1 ? "s" : ""}**.` : "";
    return { sentence, shop };
  }, [activeBag, previewBrew, tonightSugg, urgentName, shoppingPending]);

  const isAI = viewMode === "ai" || viewMode === "adaptive";

  return (
    <div className="space-y-5 pb-8">
      <LargeTitle
        title={greeting}
        subtitle={weather ? `${dayName} · ${weather.temp_f}°` : dayName}
      />

      {viewMode === "adaptive" && (
        <div className="font-sf text-[11px] font-semibold text-pronto-text-3 -mt-2">
          ◷ Adaptive · showing your {PHASE_LABEL[phase] || "morning"} view
        </div>
      )}

      {isAI && <ProntoSuggests suggestion={suggestion} onGo={() => onNavigate("coffee")} onAsk={() => onNavigate("chat")} />}

      {viewMode === "hybrid" && (
        <>
          <GlassCard radius={16} className="p-3 flex items-center gap-3">
            <div className="flex-1 font-sf text-[13.5px] text-pronto-ink">
              <span className="font-semibold">{IT.oggi}</span>{" "}
              {[activeBag && `brew ${activeBag.name}`, tonightSugg && tonightSugg.title, shoppingPending > 0 && `${shoppingPending} to buy`].filter(Boolean).join(" · ")}
            </div>
            <SecondaryButton onClick={() => onNavigate("coffee")} className="h-9 px-3">Go</SecondaryButton>
          </GlassCard>
          <MetricGrid metrics={metrics} detailed />
        </>
      )}

      {viewMode === "traditional" && (
        <>
          <MetricGrid metrics={metrics} />
          <button onClick={() => onNavigate("chat")} className="w-full">
            <GlassCard radius={16} className="p-3.5 flex items-center gap-2 text-left">
              <Eyebrow>◇</Eyebrow>
              <span className="font-sf text-[14px] text-pronto-text-2">Ask Pronto anything</span>
              <ChevronRight size={16} className="text-pronto-text-3 ml-auto" />
            </GlassCard>
          </button>
        </>
      )}
    </div>
  );
}

// AI-first / Adaptive hero.
function ProntoSuggests({ suggestion, onGo, onAsk }) {
  return (
    <>
      <GlassCard radius={24} elevated className="p-5">
        <Eyebrow className="mb-2.5">◇ Pronto suggests</Eyebrow>
        <p className="font-sf text-[16px] leading-relaxed text-pronto-ink">
          <Rich text={suggestion.sentence} />{" "}
          {suggestion.shop && <span className="text-pronto-text-2"><Rich text={suggestion.shop} /></span>}
        </p>
        <div className="flex items-center gap-2 mt-4">
          <SecondaryButton onClick={onGo} className="flex-1">{IT.andiamo} →</SecondaryButton>
          <button className="w-11 h-11 rounded-[14px] bg-[var(--pg-5)] border border-[rgba(63,74,99,0.2)] text-pronto-navy grid place-items-center" aria-label="More">⌄</button>
        </div>
      </GlassCard>
      <button onClick={onAsk} className="w-full">
        <GlassCard radius={16} className="p-3.5 flex items-center gap-2 text-left">
          <span className="font-sf text-[14px] text-pronto-text-2">Ask Pronto anything…</span>
          <ChevronRight size={16} className="text-pronto-text-3 ml-auto" />
        </GlassCard>
      </button>
    </>
  );
}

function MetricGrid({ metrics, detailed = false }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {metrics.map(m => (
        <button key={m.key} onClick={m.onClick} className="text-left">
          <GlassCard radius={18} className={`p-3.5 ${detailed ? "" : "h-[86px]"} flex flex-col`}>
            <div className="font-sf text-[11px] font-semibold uppercase tracking-[0.05em] text-pronto-text-3">{m.label}</div>
            <div className="font-round font-bold text-[17px] leading-tight text-pronto-ink truncate mt-1">{m.value}</div>
            {detailed && (
              <div className={`font-sf text-[12px] mt-0.5 truncate ${m.warn ? "text-pronto-warn" : "text-pronto-text-2"}`}>{m.detail}</div>
            )}
          </GlassCard>
        </button>
      ))}
    </div>
  );
}

// Renders **bold** spans in an otherwise plain string.
function Rich({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}
